import { useMultiStyleConfig, forwardRef } from '@chakra-ui/react-v2';

import {
  Box,
  type BoxProps,
  useRadioGroup,
  type UseRadioGroupProps,
} from '@frontend/legacy-components';

import {
  type SegmentControlStyles,
  SegmentControlStylesProvider,
} from './SegmentControlStylesContext';
import type { SegmentControlSize } from './types';
import { SegmentControlProvider } from './utils/SegmentControlContext';

export type SegmentControlProps = BoxProps & {
  size?: SegmentControlSize;
} & Pick<
    UseRadioGroupProps,
    'defaultValue' | 'isDisabled' | 'name' | 'onChange' | 'value'
  >;

/**
 * A multipart component for selecting one input among many in a segmented
 * container. Built with Chakra-UI's `useRadioGroup` hook pattern.
 */
export const SegmentControl = forwardRef<SegmentControlProps, 'div'>(
  (
    {
      size,
      defaultValue,
      isDisabled = false,
      name,
      onChange,
      value,
      children,
      ...restProps
    },
    ref,
  ) => {
    // Get multipart styling from theme
    const segmentControlStyles = useMultiStyleConfig('SegmentControl', {
      size,
    }) as SegmentControlStyles;

    // Functionality from useRadioGroup chakra hook moved internal to the component.
    const { getRootProps, getRadioProps } = useRadioGroup({
      defaultValue,

      // setting these manually to false for intended behavior
      isNative: false,
      isFocusable: false,

      isDisabled, // As of 4/5/2022, this value doesn't propogate to child radios through Chakra's hook; but included on parent in case this affects accessibility / aria roles.
      name,
      onChange,
      value,
    });
    const rootProps = getRootProps();

    return (
      <Box
        ref={ref}
        __css={segmentControlStyles.container}
        {...rootProps}
        {...restProps}
      >
        {/* Adding `isDisabled` to the provider since Chakra doesn't propogate the isDisabled property through useRadioGroup.
         * Remove if and when Chakra chooses to propogate the isDisabled property to children.
         */}
        <SegmentControlProvider value={{ getRadioProps, isDisabled }}>
          <SegmentControlStylesProvider value={segmentControlStyles}>
            {children}
          </SegmentControlStylesProvider>
        </SegmentControlProvider>
      </Box>
    );
  },
);
SegmentControl.displayName = 'SegmentControl';
